import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { BASE_URL } from "../constants";

/**
 * Marketplace sign-up page for new users.
 *
 * Two paths converge here:
 *   1. A customer who purchased on Google Cloud Marketplace arrives with a signed
 *      token as `?token=` (via the backend sign-up landing). Signing up with Google
 *      links their account to the Marketplace entitlement and approves it.
 *   2. Someone who has not purchased can still authenticate with Google, but access
 *      is not granted: they are directed to register their purchase in Marketplace.
 *
 * Existing OAuth login (Login.jsx) is unchanged.
 */
const MARKETPLACE_ORDERS_URL =
  "https://console.cloud.google.com/marketplace/orders";

const Signup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  // Google OAuth client ID, sourced from Secret Manager at build time. The client's
  // "Authorized JavaScript origins" must include this site's origin.
  const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!CLIENT_ID) {
      setError(
        "Sign-in is unavailable: this build is missing REACT_APP_GOOGLE_CLIENT_ID."
      );
    }

    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [CLIENT_ID]);

  const linkMarketplaceAccount = async (userInfo) => {
    const res = await fetch(`${BASE_URL}/api/marketplace/signup`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token,
        email: userInfo.email,
        name: userInfo.name || userInfo.email,
        hosted_domain: userInfo.hd || "",
        google_user_id: userInfo.sub || "",
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.detail || "Sign-up failed. Please try again.");
    }
    return data;
  };

  const handleGoogleSignup = () => {
    setError("");
    setNotice("");
    setSuccess("");
    if (!CLIENT_ID) {
      setError(
        "Sign-in is unavailable: this build is missing REACT_APP_GOOGLE_CLIENT_ID."
      );
      return;
    }
    try {
      const google = window.google;
      const client = google.accounts.oauth2.initTokenClient({
        client_id: CLIENT_ID,
        scope:
          "https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/userinfo.email",
        callback: async (response) => {
          if (!response.access_token) return;
          setLoading(true);
          try {
            const userInfoResponse = await fetch(
              "https://www.googleapis.com/oauth2/v3/userinfo",
              { headers: { Authorization: `Bearer ${response.access_token}` } }
            );
            if (!userInfoResponse.ok) {
              throw new Error("Failed to get user info");
            }
            const userInfo = await userInfoResponse.json();

            // No purchase token: authenticated, but not entitled. Direct the user
            // to register their purchase in Marketplace rather than granting access.
            if (!token) {
              setNotice(
                "To activate your subscription, register your purchase in Google " +
                  "Cloud Marketplace, then return here to finish setting up your account."
              );
              return;
            }

            const data = await linkMarketplaceAccount(userInfo);
            setSuccess(
              data.status === "approved"
                ? "Your account is set up. Redirecting you to sign in..."
                : "Account created. Access will activate once approval completes."
            );
            setTimeout(() => navigate("/login"), 2500);
          } catch (err) {
            setError(err.message || "Sign-up failed. Please try again.");
          } finally {
            setLoading(false);
          }
        },
        error_callback: () => {
          setError("Authentication failed. Please try again.");
        },
      });
      client.requestAccessToken();
    } catch (err) {
      setError("An error occurred during sign-up. Please try again.");
    }
  };

  const steps = [
    "Register your purchase in Marketplace",
    "Sign in with your Google account",
    "Start remediating your documents",
  ];

  return (
    <div className="fixed inset-0 overflow-y-auto bg-gray-50 flex flex-col md:flex-row">
      {/* Left brand panel */}
      <div className="md:w-1/2 bg-gradient-to-br from-blue-700 to-blue-500 text-white px-8 py-12 md:p-14 flex flex-col justify-center">
        <div className="flex items-center gap-3 mb-10">
          <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center">
            <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <circle cx="12" cy="4" r="2" />
              <path d="M21 9c0 .55-.45 1-1 1h-4v3l1.6 6.4a1 1 0 0 1-1.94.5L14 15h-4l-1.66 4.9a1 1 0 1 1-1.94-.5L8 13v-3H4a1 1 0 1 1 0-2h16c.55 0 1 .45 1 1z" />
            </svg>
          </div>
          <div>
            <div className="text-xl font-bold leading-tight">ADA Compliance</div>
            <div className="text-blue-100 text-sm">Document Accessibility</div>
          </div>
        </div>

        <div className="text-blue-100 text-sm font-medium mb-3">
          Google Cloud Marketplace
        </div>
        <h1 className="text-3xl md:text-4xl font-bold leading-tight mb-4">
          Your next step toward accessible documents.
        </h1>
        <p className="text-blue-100 text-base md:text-lg mb-10 max-w-md">
          Remediate PDFs to WCAG 2.1 AA and meet ADA requirements automatically,
          all in one workspace.
        </p>

        <ol className="space-y-5">
          {steps.map((label, i) => (
            <li key={i} className="flex items-center gap-4">
              <span className="w-8 h-8 rounded-full border border-white/40 flex items-center justify-center text-sm font-semibold shrink-0">
                {i + 1}
              </span>
              <span className="text-blue-50">{label}</span>
            </li>
          ))}
        </ol>
      </div>

      {/* Right card panel */}
      <div className="md:w-1/2 flex items-center justify-center p-6 md:p-8 bg-gray-50">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
          <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mb-6">
            <svg className="w-6 h-6 text-blue-600" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <circle cx="12" cy="4" r="2" />
              <path d="M21 9c0 .55-.45 1-1 1h-4v3l1.6 6.4a1 1 0 0 1-1.94.5L14 15h-4l-1.66 4.9a1 1 0 1 1-1.94-.5L8 13v-3H4a1 1 0 1 1 0-2h16c.55 0 1 .45 1 1z" />
            </svg>
          </div>

          <h2 className="text-2xl font-semibold text-gray-900 mb-2">
            Get started with ADA Compliance
          </h2>
          <p className="text-gray-600 text-sm mb-6">
            Use your Google account to access ADA Compliance. Purchased through
            Google Cloud Marketplace? Open your order and select{" "}
            <span className="font-semibold">Register</span> to finish setting up
            your purchase.
          </p>

          {error && (
            <div className="mb-4 rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}
          {notice && (
            <div className="mb-4 rounded-md bg-blue-50 border border-blue-200 px-4 py-3 text-sm text-blue-800">
              {notice}
            </div>
          )}
          {success && (
            <div className="mb-4 rounded-md bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700">
              {success}
            </div>
          )}

          <button
            onClick={handleGoogleSignup}
            disabled={loading || !CLIENT_ID}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-md bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            <span className="bg-white rounded-sm p-1 flex items-center justify-center">
              <svg className="w-4 h-4" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
            </span>
            {loading ? "Setting up..." : "Sign up with Google"}
          </button>

          <a
            href={MARKETPLACE_ORDERS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="block text-center text-blue-600 hover:text-blue-700 text-sm font-medium mt-4"
          >
            Open Marketplace orders
          </a>

          <div className="flex items-center justify-center gap-2 text-xs text-gray-500 mt-6">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M12 1 3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z" />
            </svg>
            Secure sign-in with Google. No additional password to remember.
          </div>

          <p className="text-center text-sm text-gray-600 mt-6">
            Already have an account?{" "}
            <button
              type="button"
              onClick={() => navigate("/login")}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Sign in
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Signup;
